import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { OBSIDIAN } from '../../constants/obsidianDashboardTheme';
import type { ICampaignPremiumAccess } from '../../types/global/campaignPremiumAccess.types';

const entitlementLabel = (e: string) =>
    e === 'instagram_automation' ? 'Instagram automation' : e === 'auto_upload' ? 'Auto-upload' : e;

const ctaSx = {
    alignSelf: 'flex-start',
    px: 2.5,
    py: 1.1,
    borderRadius: '10px',
    textTransform: 'none' as const,
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: '0.01em',
    background: `linear-gradient(90deg, ${OBSIDIAN.primary} 0%, ${OBSIDIAN.primaryDim} 100%)`,
    color: OBSIDIAN.onPrimaryFixed,
    boxShadow: '0 4px 18px rgba(255, 201, 101, 0.28)',
    '&:hover': {
        background: `linear-gradient(90deg, #ffd080 0%, ${OBSIDIAN.primaryDim} 100%)`,
    },
};

const outlineCtaSx = {
    alignSelf: 'flex-start',
    px: 2.5,
    py: 1.1,
    borderRadius: '10px',
    textTransform: 'none' as const,
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    fontWeight: 700,
    fontSize: 14,
    borderColor: 'rgba(255,201,101,0.45)',
    color: OBSIDIAN.primary,
    '&:hover': {
        borderColor: OBSIDIAN.primary,
        bgcolor: 'rgba(255,201,101,0.08)',
    },
};

export interface CampaignPremiumAccessGateProps {
    access?: ICampaignPremiumAccess | null;
    isSignedIn?: boolean;
    onSubscribe?: () => void;
    onConnectAccounts?: () => void;
    onSignIn?: () => void;
    sx?: object;
}

export function CampaignPremiumAccessGate({
    access,
    isSignedIn,
    onSubscribe,
    onConnectAccounts,
    onSignIn,
    sx,
}: CampaignPremiumAccessGateProps) {
    if (!access?.premium || access.can_apply) return null;

    const code = access.blocked_reason_code;
    const message =
        isSignedIn && code === 'login_required'
            ? 'Checking your plan and connected accounts…'
            : access.blocked_reason_message?.trim() ||
              'This premium campaign requires a Pro plan and connected social accounts.';

    // Prefer live auth over a stale anonymous premium_access payload.
    const showSignIn = !isSignedIn;
    const showSubscribe =
        code === 'subscription_required' ||
        code === 'login_required' ||
        Boolean(access.subscription_required && access.missing_entitlements?.length);
    const showConnect =
        code === 'connected_accounts_required' ||
        code === 'instagram_automation_scopes' ||
        Boolean(isSignedIn && access.missing_connected_platforms?.length);

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '14px',
                bgcolor: OBSIDIAN.surfaceContainerHigh,
                border: `1px solid ${OBSIDIAN.surfaceContainerHighest}`,
                boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
                ...sx,
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: `linear-gradient(180deg, ${OBSIDIAN.primary} 0%, ${OBSIDIAN.primaryDim} 100%)`,
                }}
            />
            <Box sx={{ pl: 2.75, pr: 2.5, py: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: 'rgba(255,201,101,0.12)',
                            border: '1px solid rgba(255,201,101,0.28)',
                            flexShrink: 0,
                        }}>
                        <WorkspacePremiumOutlinedIcon sx={{ fontSize: 20, color: OBSIDIAN.primary }} />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: OBSIDIAN.primary,
                                lineHeight: 1.2,
                            }}>
                            Premium access
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                fontSize: { xs: 17, sm: 18 },
                                color: OBSIDIAN.onSurface,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.25,
                                mt: 0.25,
                            }}>
                            Premium campaign requirements
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 14,
                        color: 'rgba(255,255,255,0.88)',
                        lineHeight: 1.55,
                        mb: 1.75,
                    }}>
                    {message}
                </Typography>

                {(access.missing_entitlements?.length || access.missing_connected_platforms?.length) && (
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                        {access.missing_entitlements?.map(e => (
                            <Chip
                                key={e}
                                size="small"
                                label={entitlementLabel(e)}
                                sx={{
                                    height: 28,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontWeight: 600,
                                    fontSize: 12,
                                    bgcolor: 'rgba(255,201,101,0.1)',
                                    color: OBSIDIAN.primary,
                                    border: '1px solid rgba(255,201,101,0.28)',
                                }}
                            />
                        ))}
                        {access.missing_connected_platforms?.map(p => (
                            <Chip
                                key={p}
                                size="small"
                                label={`Connect ${p}`}
                                sx={{
                                    height: 28,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontWeight: 600,
                                    fontSize: 12,
                                    bgcolor: OBSIDIAN.surfaceContainerHighest,
                                    color: OBSIDIAN.onSurface,
                                    border: `1px solid ${OBSIDIAN.outline}55`,
                                    textTransform: 'capitalize',
                                }}
                            />
                        ))}
                    </Stack>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    {showSignIn && onSignIn ? (
                        <Button size="medium" variant="contained" onClick={onSignIn} sx={ctaSx}>
                            Sign in
                        </Button>
                    ) : null}
                    {showSubscribe && onSubscribe && isSignedIn ? (
                        <Button size="medium" variant="contained" onClick={onSubscribe} sx={ctaSx}>
                            View plans
                        </Button>
                    ) : null}
                    {showConnect && onConnectAccounts && isSignedIn ? (
                        <Button size="medium" variant="outlined" onClick={onConnectAccounts} sx={outlineCtaSx}>
                            {code === 'instagram_automation_scopes' ? 'Reconnect Instagram' : 'Connect accounts'}
                        </Button>
                    ) : null}
                </Stack>
            </Box>
        </Box>
    );
}
